import { css, useTheme } from '@emotion/react';
import { Spin, Button } from 'antd';
import React, { type CSSProperties } from 'react';
import { legacyOnly, newDesignOnly, type Env0Theme } from 'constants/themes.constants';
import styled from 'types/theme.types';
import { Link, type LinkProps } from 'react-router-dom';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { legacyThemes } from 'constants/themes.constants.legacy';

export type ButtonSize = 'small' | 'large';
export type ButtonType = 'primary' | 'default' | 'danger' | 'link';

export interface CustomButtonProps {
  id?: string;
  type?: ButtonType;
  shape?: 'round' | 'circle';
  size?: ButtonSize;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  href?: string;
  isExternalLink?: boolean;
  isLoading?: boolean;
  addPlus?: boolean;
  block?: boolean;
  flatBottom?: boolean;
  style?: CSSProperties;
  className?: string;
  invertColor?: boolean;
  'data-e2e'?: string;
  'aria-label'?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const CustomButton = ({
  type = 'default',
  onClick,
  children,
  href,
  isExternalLink,
  isLoading = false,
  addPlus = false,
  ...properties
}: CustomButtonProps) => {
  const theme = useTheme();

  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!href) event.preventDefault();
    onClick?.(event);
  };

  // Switch-off pins the literals the old module-scope theme read produced - always the light theme,
  // so the spinner is inverted in dark. Reproducing that is D7.7; APO-484 deletes the branch.
  const loaderColor = theme.isNewDesign
    ? type === 'default' || type === 'link'
      ? 'var(--icon-primary)'
      : 'var(--text-on-action)'
    : type === 'default'
      ? legacyThemes.light.primaryBlack
      : legacyThemes.light.primaryWhite;
  const loader = <LoadingOutlined style={{ fontSize: 18, color: loaderColor }} spin />;

  // Figma's Tertiary is antd's `text`, not its `link` (a hyperlink). Legacy keeps `link`.
  const buttonType = theme.isNewDesign && type === 'link' ? 'text' : type;

  // Content stays in the layout at opacity 0 with the spinner overlaid, so the button holds its width
  // without a min-width. Opacity, not visibility or unmounting: those drop the label out of the
  // accessibility tree while the button is busy.
  const styledButton = theme.isNewDesign ? (
    <StyledButton
      onClick={onClickHandler}
      type={buttonType}
      role={href ? 'link' : 'button'}
      aria-busy={isLoading || undefined}
      data-loading={isLoading || undefined}
      {...properties}>
      {addPlus && <PlusOutlined />}
      {children}
      {isLoading && (
        <span data-loading-indicator>
          <Spin size="small" indicator={loader} />
        </span>
      )}
    </StyledButton>
  ) : (
    <StyledButton onClick={onClickHandler} type={buttonType} role={href ? 'link' : 'button'} {...properties}>
      {addPlus && !isLoading && <span>+&nbsp;</span>}
      {isLoading ? <Spin style={{ height: 18 }} size="small" indicator={loader} /> : children}
    </StyledButton>
  );

  if (href) {
    return isExternalLink ? (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
        {styledButton}
      </a>
    ) : (
      <StyledLink
        to={href}
        onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => e.stopPropagation()}
        block={properties.block}>
        {styledButton}
      </StyledLink>
    );
  } else {
    return styledButton;
  }
};

export const StyledLink = styled(({ block, ...props }: LinkProps & { block?: boolean }) => <Link {...props} />)`
  border-radius: 4px;
  ${({ block }) => (block ? 'width: 100%;' : '')}
`;

const dangerCss = (theme: Env0Theme) => css`
  &:disabled {
    background-color: ${theme.primaryDisabledGray} !important;
    border: 1px solid ${theme.primaryDisabledGray} !important;
    color: ${theme.primaryWhite} !important;
    cursor: not-allowed;
  }
`;

const defaultCss = (theme: Env0Theme) => css`
  &:disabled {
    background-color: ${theme.primaryWhite} !important;
    border: 1px solid ${theme.secondaryDisabledGray} !important;
    color: ${theme.secondaryDisabledGray} !important;
    cursor: not-allowed;
  }
`;

const primaryCss = (theme: Env0Theme) => css`
  &:disabled {
    background-color: ${theme.primaryDisabledGray} !important;
    border: 1px solid ${theme.primaryDisabledBorder} !important;
    color: ${theme.primaryDisabledText} !important;
    cursor: not-allowed;
  }
`;

const smallSizeCss = css`
  font-size: 12px !important;
  min-width: 120px !important;
  padding: 5px 10px !important;
`;

const defaultSizeCss = css`
  font-size: 13px !important;
  min-width: 130px !important;
  padding: 5px 25px !important;
`;

const largeSizeCss = css`
  font-size: 15px !important;
  min-width: 170px !important;
  padding: 10px 25px !important;
`;

const sizeStyleMap: Record<ButtonSize | 'default', any> = {
  small: smallSizeCss,
  default: defaultSizeCss,
  large: largeSizeCss
};

// `text` never reaches this at runtime - the map is not emitted on the new-design path. Null keeps
// the record total.
const getTypeStyleMap = (theme: Env0Theme): Record<ButtonType | 'text', any> => ({
  danger: dangerCss(theme),
  primary: primaryCss(theme),
  default: defaultCss(theme),
  link: null,
  text: null
});

// Not in the bridge: 5.6.4 derives a default button's hover from colorPrimaryHover, which the primary
// button's hover fill also needs at a different value, and has no defaultHoverBg at all.
const secondaryHoverCss = css`
  background-color: var(--surface-secondary-action-hover);
  border-color: var(--border-secondary-hover);
  /* --text-heading, not --text-on-action: the latter is black in both modes for the lime fill, and
     this hover fill is dark olive in dark mode. Same value in light. */
  color: var(--text-heading);
`;

// `text` is internal: the public prop stays `link`, and CustomButton maps it on the new-design path.
type ButtonExtendedType = Omit<CustomButtonProps, 'type'> & { role: string; type?: ButtonType | 'text' };

// Emotion passes the theme in with the props; the component's own type does not declare it, and the
// branch helpers read it off the same object.
type StyledButtonProps = ButtonExtendedType & { theme: Env0Theme };

const StyledButton = styled<React.ComponentType<ButtonExtendedType>>(
  ({ type, ...props }) => (
    <Button type={type === 'danger' ? 'primary' : type} danger={type === 'danger'} {...(props as any)} />
  ),
  {
    shouldForwardProp: propName => propName !== 'flatBottom' && propName !== 'invertColor'
  }
)`
  ${(props: StyledButtonProps) =>
    legacyOnly(
      theme => css`
        ${sizeStyleMap[props.size || 'default']}
        ${getTypeStyleMap(theme)[props.type || 'default']}

        &:hover:enabled {
          ${!props.disabled && 'box-shadow: 0px 1px 3px 0px rgba(0,0,0,0.3) !important;'}
        }
      `
    )(props)}

  ${(props: StyledButtonProps) =>
    newDesignOnly(
      theme => css`
        /* No emotion focus ring: antd's genFocusStyle draws it for every button, and the theme pins
           its pair (lineWidthFocus + colorPrimaryBorder in antd.constants) to the same ring the text
           fields use — one focus system app-wide (UI-polish review, item B). The old 1px emotion ring
           lost the cascade to antd's and never rendered. */

        /* antd 5.6.4 spaces an icon from its label with the selector "> .anticon + span", which only
           fires when the label is a span sibling. A button whose child is a bare text node never
           matches it, so icon and label collide - which is why several call sites hand-rolled a gap.

           Only display needs the doubled class: antd declares inline-block on .ant-btn behind a
           zero-specificity :where(), which a single class only ties. It declares no justify-content,
           align-items or gap, so those stay at ordinary specificity where a call site still wins by
           source order - command-card, analysis-feedback and two tables rely on that. */
        && {
          display: inline-flex;
        }
        align-items: center;
        justify-content: center;
        gap: ${theme.spacing.xs};

        /* Loading holds the width: content keeps its box at opacity 0, spinner overlaid. */
        &[data-loading] {
          position: relative;
          cursor: default;

          > *:not([data-loading-indicator]) {
            opacity: 0;
          }

          /* Bare text children are not elements, so opacity cannot reach them. */
          color: transparent;

          [data-loading-indicator] {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }

        /* Scoping through antd's own class beats its selector without !important. */
        &&.ant-btn-default:not(:disabled):hover {
          ${secondaryHoverCss}
        }

        /* Figma specs no Pressed state; matching Hovered is the extrapolation (D1). */
        &&.ant-btn-default:not(:disabled):active {
          ${secondaryHoverCss}
        }

        /* Only the button's own leading icon — a direct .anticon child (addPlus) or antd's icon slot.
           A blanket "&& svg" also blew the dropdown chevron up to 16px beside 14px text (UI-polish
           review, "+ Add Variable"). */
        && > .anticon svg,
        && > .ant-btn-icon svg {
          font-size: 16px;
        }

        /* antd hardcodes small's horizontal padding to 8, which is not configurable through the theme
           and left "Run Now" hugging its label (UI-polish review, item K). */
        &&.ant-btn-sm {
          padding-inline: ${theme.padding.md};
        }

        /* antd draws a disabled button's border from colorBorder — the lime border.secondary — which
           put a brand-coloured edge on a state that should recede (UI-polish review, item J). */
        &&:disabled {
          border-color: var(--border-disabled);
        }

        ${
          props.type === 'text' &&
          css`
            padding-inline: ${theme.padding.md};

            &:not(:disabled):hover {
              background-color: var(--surface-secondary-action-hover);
              color: var(--text-heading);
            }
          `
        }

        ${
          props.type === 'danger' &&
          css`
            /* Text-error, per the destructive actions in the redesign frames (Destroy 267:6858,
               Archive project 254:32132): no fill, no border, error label — tint fill on hover. The
               token set has no on-error text (colorTextLightSolid is the on-lime black, ~3:1 on a red
               fill), which rules the solid variant out anyway. Doubled class + antd's own, because
               antd paints primary-danger at the same specificity and is injected later. */
            &&.ant-btn-dangerous {
              background-color: transparent;
              border-color: transparent;
              color: var(--text-error);
            }

            &&.ant-btn-dangerous:not(:disabled):hover {
              background-color: var(--surface-error-background);
              border-color: transparent;
              color: var(--text-error);
            }
          `
        }
      `
    )(props)}

  .ant-spin-dot-item {
    background-color: ${props => (props.invertColor ? props.theme.primaryBlack : props.theme.primaryWhite)};
  }

  ${(props: ButtonExtendedType) =>
    props.flatBottom &&
    `
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  `}
`;

/**
 * A Fix
 * Mouse events don't trigger at disabled button in Chrome
 *
 * This fixes the scenario when we wrap a disabled button with a Tooltip.
 * Without this fix the Tooltip message will not disappear "onmouseleave"
 *
 * relevant snippet: https://github.com/ant-design/ant-design/pull/4865/files#diff-186839a30bf8b9d67a4b10bf7c091d5f
 */
CustomButton.__ANT_BUTTON = true;

// Passing a size switch-off would also switch on the legacy sizeStyleMap entry, adding a min-width the
// site never had. APO-484 inlines these.
export const newDesignSize = (theme: Env0Theme, size: ButtonSize) => (theme.isNewDesign ? size : undefined);

// Same guard for `type`: passing `link` switch-off would repaint the site as an antd link.
export const newDesignType = (theme: Env0Theme, type: ButtonType) => (theme.isNewDesign ? type : undefined);

export default CustomButton;

// Figma has no dark-fill variant, so the new design renders a plain Secondary and this block is
// switch-off only. APO-484 deletes the export and repoints RunFromHereButton and AiAnalysisButton.
export const BlueButton = styled(CustomButton)`
  ${legacyOnly(
    theme => css`
      &:active,
      &:hover:enabled {
        background-color: ${theme.tertiaryGray} !important;
        border: 1px solid ${theme.tertiaryGray} !important;
        color: ${theme.whiteColor} !important;
      }

      &:disabled {
        background-color: ${theme.primaryDisabledGray} !important;
        border: 1px solid ${theme.primaryDisabledBorder} !important;
        color: ${theme.primaryDisabledText} !important;
        cursor: not-allowed;
      }

      background-color: ${theme.tertiaryGray};
      border: 1px solid ${theme.tertiaryGray} !important;
      color: ${theme.whiteColor};

      // for some reason the icon and text are not centered by <Button/>, so I needed to override it
      display: inline-flex;
      align-items: center;
      justify-content: center;
      & span:nth-child(2) {
        margin-bottom: -2px;
      }
    `
  )}
`;
